import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

/* CheckStat Free 6.0 — de externe bezoekersteller van support.leza.nl.
 *
 * Het script dat CheckStat aanlevert kan hier NIET één-op-één in. Het bouwt
 * zichzelf op met document.write(), en dat is na het laden van een pagina geen
 * schrijven meer maar een wissen: de browser opent dan impliciet een nieuw
 * document en gooit de bestaande DOM — inclusief de hele React-app — weg. Het
 * schrijft bovendien een reeks <script language="javascript1.x">-tags om de
 * JavaScript-versie te peilen, een truc uit het Netscape-tijdperk.
 *
 * Wat het script uiteindelijk DOET is één ding: een afbeelding ophalen bij
 * count.cgi met de pagina, het scherm en de verwijzende site erachter. Dat is
 * wat hieronder staat, en verder niets. Geen cookie, geen script van derden in
 * de pagina, geen invloed op de opbouw.
 */

/** Het CheckStat-account. Gaat als eerste, naamloze parameter mee. */
const ACCOUNT = 'supportleza';

/* Twee paden, en ze zijn niet uitwisselbaar: cgi-exe hoort bij de onzichtbare
 * meting (de 1×1), cgi-bin bij de zichtbare telknop van 19×19. We meten
 * onzichtbaar — in het aangeleverde script is dat de stand '010' in plaats van
 * '110'. Wil je de knop alsnog tonen, dan is dit niet het enige dat verandert;
 * zie de uitleg in de README. */
const TELLER = 'https://www.checkstat.nl/cgi-exe/count.cgi';

/**
 * Telt één paginaweergave per URL-wissel, óók bij zachte navigatie.
 *
 * Zonder dat laatste zou alleen de pagina waarmee iemand binnenkomt geteld
 * worden: de rest van een bezoek gaat via React Router en dat is geen nieuwe
 * paginalading. Het meetelt op pad *en* querystring, want in de oude EJS-app
 * was elke filterklik op /search een echte paginalading en dus ook een tik in
 * CheckStat. Zo blijven de cijfers vergelijkbaar met wat er in het archief van
 * de teller staat.
 */
export function CheckStat() {
  const { pathname, search } = useLocation();
  // Waar de bezoeker vandaan kwam. Bij een zachte navigatie staat er in
  // document.referrer nog steeds de EXTERNE verwijzer van het binnenkomen;
  // CheckStat verwacht daar de vorige pagina, net als bij een echte lading.
  const vorige = useRef<string | null>(null);
  const geteld = useRef<string | null>(null);

  useEffect(() => {
    const hier = pathname + search;
    // Dubbel monteren in ontwikkelmodus mag geen dubbele tik geven.
    if (geteld.current === hier) return;
    const binnenkomst = geteld.current === null;
    geteld.current = hier;

    const scherm = window.screen;
    // javaEnabled() is uit de moderne typedefinities verdwenen maar bestaat in
    // browsers nog wél — en geeft daar altijd false.
    const nav = navigator as Navigator & { javaEnabled?: () => boolean };

    const query = [
      ACCOUNT,
      `location=${encodeURIComponent(window.location.href)}`,
      `screensize=${scherm.width}*${scherm.height}`,
      `screencolors=${scherm.pixelDepth || scherm.colorDepth}`,
      // Het origineel peilt dit met versiegebonden <script>-tags; élke browser
      // die vandaag bestaat komt daar op 1.5 uit. Meten wat je al weet levert
      // hier niets op, en de meetmethode zelf kan niet mee.
      'javascript=1.5',
      `java=${nav.javaEnabled?.() ? 'y' : 'n'}`,
      `referrer=${encodeURIComponent(binnenkomst ? document.referrer : (vorige.current ?? ''))}`,
      // Tegen de cache van de browser: zonder dit telt dezelfde pagina binnen
      // één bezoek maar één keer.
      `time=${Date.now()}`,
    ].join('&');

    vorige.current = window.location.href;

    // Een losse Image() en geen <img> in de opbouw: de teller hoort niets te
    // zijn dat kan verspringen, en een mislukt verzoek — adblocker, CheckStat
    // eruit — mag de pagina niet raken.
    const pixel = new Image();
    pixel.referrerPolicy = 'no-referrer-when-downgrade';
    pixel.src = `${TELLER}?${query}`;
  }, [pathname, search]);

  return null;
}
